import React, { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, ArrowRight, CheckCircle2, ClipboardCheck, Clock3, MessageSquare, ShieldCheck, XCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { apiGet, apiPost } from '../../lib/api'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'

type ReviewAction = 'approved' | 'changes_requested' | 'rejected'

interface ApprovalItem {
  artifact_id: string
  artifact_version_id: string
  version: number
  conversation_id?: string | null
  connection_name: string
  connector_family: string
  proposed_action: string
  requester_name: string
  requested_at: string
  risk: 'low' | 'medium' | 'high'
  validation: { status: string; counts: Record<string, number>; tested_at?: string | null }
  evidence: { added: number; modified: number; removed: number; destructive: number }
  can_approve: boolean
  latest_review?: { action: string; comment?: string | null; created_at: string } | null
}

interface ApprovalInboxResponse {
  items: ApprovalItem[]
  counts: { total: number; high: number; ready: number }
}

const ACTION_LABELS: Record<ReviewAction, string> = {
  approved: 'Approve version',
  changes_requested: 'Request changes',
  rejected: 'Reject version',
}

export default function ApprovalInboxPanel({ projectId, onArtifactRefresh }: { projectId: string; onArtifactRefresh?: () => void }) {
  const navigate = useNavigate()
  const [data, setData] = useState<ApprovalInboxResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reviewing, setReviewing] = useState<{ item: ApprovalItem; action: ReviewAction; comment: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try { setData(await apiGet(`/api/artifacts/project/${projectId}/approvals`)) }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to load approvals') }
    finally { setLoading(false) }
  }, [projectId])

  useEffect(() => { load() }, [load])

  const submit = async () => {
    if (!reviewing || submitting) return
    if (reviewing.action !== 'approved' && !reviewing.comment.trim()) {
      toast.error('Add a comment explaining the requested change or rejection')
      return
    }
    setSubmitting(true)
    try {
      await apiPost(`/api/artifacts/${reviewing.item.artifact_id}/versions/${reviewing.item.version}/reviews`, {
        action: reviewing.action,
        comment: reviewing.comment.trim() || null,
      })
      toast.success(reviewing.action === 'approved' ? 'Version approved' : reviewing.action === 'rejected' ? 'Version rejected' : 'Changes requested')
      setReviewing(null)
      await load()
      onArtifactRefresh?.()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Review failed')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading && !data) return <div className="approval-inbox-state"><ClipboardCheck size={19} /><p>Loading pending approvals…</p></div>
  if (error && !data) return <div className="approval-inbox-state error"><AlertTriangle size={19} /><p>{error}</p><button onClick={load}>Try again</button></div>

  return (
    <div className="approval-inbox">
      <div className="approval-inbox-head">
        <span className="workspace-kicker">Project queue</span>
        <h3>Approval inbox</h3>
        <p>Review immutable versions across every connection in this project.</p>
      </div>

      <div className="approval-inbox-counts">
        <span><strong>{data?.counts.total || 0}</strong> pending</span>
        <span><strong>{data?.counts.ready || 0}</strong> ready</span>
        <span className={data?.counts.high ? 'danger' : ''}><strong>{data?.counts.high || 0}</strong> high risk</span>
      </div>

      {!data?.items.length ? (
        <div className="approval-inbox-empty"><ShieldCheck size={22} /><strong>Nothing waiting for review</strong><p>New configuration versions appear here when they require an explicit decision.</p></div>
      ) : data.items.map(item => (
        <article className={`approval-inbox-card risk-${item.risk}`} key={item.artifact_version_id}>
          <div className="approval-inbox-card-head">
            <div>
              <span className={`approval-risk ${item.risk}`}>{item.risk} risk</span>
              <h4>{item.connection_name}</h4>
              <p>{item.proposed_action} · {item.connector_family} v{item.version}</p>
            </div>
            {item.conversation_id && (
              <button className="approval-open-chat" onClick={() => navigate(`/project/${projectId}/chat/${item.conversation_id}`)} title="Open source conversation">
                <MessageSquare size={12} /><ArrowRight size={11} />
              </button>
            )}
          </div>

          <div className="approval-request-meta">
            <span>Requested by {item.requester_name}</span>
            <span><Clock3 size={10} /> {new Date(item.requested_at).toLocaleString()}</span>
          </div>

          <div className="approval-evidence-grid">
            <span><strong>{item.evidence.added}</strong> added</span>
            <span><strong>{item.evidence.modified}</strong> changed</span>
            <span className={item.evidence.removed ? 'danger' : ''}><strong>{item.evidence.removed}</strong> removed</span>
            <span className={item.evidence.destructive ? 'danger' : ''}><strong>{item.evidence.destructive}</strong> destructive</span>
          </div>

          <div className={`approval-validation ${item.validation.status}`}>
            {item.can_approve ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
            <span>Validation {item.validation.status.replaceAll('_', ' ')}</span>
          </div>

          {item.latest_review?.comment && <p className="approval-latest-comment">Latest feedback: {item.latest_review.comment}</p>}

          {reviewing?.item.artifact_version_id === item.artifact_version_id ? (
            <div className="approval-decision-box">
              <strong>{ACTION_LABELS[reviewing.action]}</strong>
              <p>{item.proposed_action} for <b>{item.connection_name} v{item.version}</b>. This decision applies only to this immutable version.</p>
              <Textarea value={reviewing.comment} onChange={event => setReviewing({ ...reviewing, comment: event.target.value })} rows={3} maxLength={2000} placeholder={reviewing.action === 'approved' ? 'Optional approval note' : 'Required explanation'} />
              <div><Button variant="ghost" size="sm" onClick={() => setReviewing(null)}>Cancel</Button><Button size="sm" variant={reviewing.action === 'rejected' ? 'destructive' : 'default'} disabled={submitting || (reviewing.action !== 'approved' && !reviewing.comment.trim())} onClick={submit}>{submitting ? 'Saving…' : ACTION_LABELS[reviewing.action]}</Button></div>
            </div>
          ) : (
            <div className="approval-card-actions">
              <Button size="sm" disabled={!item.can_approve} onClick={() => setReviewing({ item, action: 'approved', comment: '' })}><CheckCircle2 size={12} /> Approve</Button>
              <Button size="sm" variant="outline" onClick={() => setReviewing({ item, action: 'changes_requested', comment: '' })}><AlertTriangle size={12} /> Changes</Button>
              <Button size="sm" variant="ghost" onClick={() => setReviewing({ item, action: 'rejected', comment: '' })}><XCircle size={12} /> Reject</Button>
            </div>
          )}
          {!item.can_approve && <p className="approval-lock-note">Approval remains locked until this exact version passes validation.</p>}
        </article>
      ))}
    </div>
  )
}
